import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	approveMembershipRequest,
	CommunityMembershipRequestListItem,
	declineMembershipRequest,
	listMembershipRequests
} from "client/Api/Communities/communityMembershipRequests";
import {
	DateString,
	IconButton,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig
} from "client/Components";
import { contains } from "client/Data/searching";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import {
	AlertManager,
	createLocalDataSource,
	useAlerts,
	useSortHandler,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import { getComputedDisplayName } from "client/Features/AddressBook/common";
import { resolveFormattedTextString } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { HubManagementDialogContentLayout } from "../HubManagementDialogContentLayout";
import styles from "./CommunityMembershipRequests.module.less";

export type CommunityMembershipRequestsProps = {
	isMobile: boolean;
	isResponsive: boolean;
};

export const CommunityMembershipRequests = (props: CommunityMembershipRequestsProps): ReactElement => {
	const {
		isMobile,
		isResponsive
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [items, setItems] = useState<readonly MembershipRequestGridItem[]>([]);
	const [loadingIds, setLoadingIds] = useState<ReadonlySet<number>>(new Set());
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<MembershipRequestGridItem>>({
		propertyName: "requestedOn",
		direction: DataSourceSortDirection.Descending
	});

	const { sortingFor } = useSortHandler(sortingState, setSortingState);
	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	useEffect(
		() => setCurrentPageNumber(0),
		[searchTerm, sortingState]
	);

	const {
		currentItems,
		pagination
	} = useDataSource(
		items,
		{
			searchTerm,
			sorting: sortingState,
			pagination: {
				currentPageNumber
			}
		}
	);

	useEffect(
		(): EffectWithDestructorResult => {
			const fetchRequests = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const requests: readonly CommunityMembershipRequestListItem[] = await listMembershipRequests(signal);

					setItems(requests.map(convertToGridItem));
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			};

			const cancellation = new AbortController();
			fetchRequests(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const respondHandler = useCallback(
		async (item: MembershipRequestGridItem, response: MembershipRequestResponse): Promise<void> => {
			setLoadingIds(x => new Set(x).add(item.id));

			try {
				let successMessageKey: LocalizationKey;

				switch (response) {
					case MembershipRequestResponse.Approve:
						await approveMembershipRequest(item.id, globalCancellation);
						successMessageKey = "_NameWasApprovedForCommunityName";
						break;
					case MembershipRequestResponse.Decline:
						await declineMembershipRequest(item.id, globalCancellation);
						successMessageKey = "_NameWasDeclinedForCommunityName";
						break;
					default:
						never(response);
				}

				setItems(x => x.filter(y => y.id !== item.id));

				alertManager.showSuccess(
					resolveFormattedTextString({
						template: localize(successMessageKey),
						replacements: [
							{
								what: "{{Name}}",
								filler: item.requestedBy
							},
							{
								what: "{{CommunityName}}",
								filler: item.communityName
							}
						]
					})
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}

			setLoadingIds(
				x => {
					const newSet: Set<number> = new Set(x);
					newSet.delete(item.id);

					return newSet;
				}
			);
		},
		[alertManager, globalCancellation]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "users",
				title: localize("_NoRequestsYet"),
				message: localize("_CurrentlyThereAreNoRequests")
			};
		},
		[searchTerm]
	);

	return (
		<HubManagementDialogContentLayout
			title={localize("_MembershipRequests")}
			isMobile={isMobile}
			isResponsive={isResponsive}
			trackingId="community-membership-requests"
			isBusy={isBusy}
		>
			<HubManagementDialogContentLayout.Filter>
				<SearchInputForm
					trackingId="search-form"
					onChange={setSearchTerm}
					placeholder={
						<SearchPlaceholder
							kind="filter"
							properties={[
								localize("_RequestedBy"),
								localize("_Community")
							]}
						/>
					}
					height="large"
					icon="filter"
				/>
			</HubManagementDialogContentLayout.Filter>
			<HubManagementDialogContentLayout.Table>
				<TableView
					items={currentItems}
					itemKey="id"
					height="stretch"
					noResultConfig={noResultConfig}
					columns={[
						{
							title: localize("_Actions"),
							trackingId: "actions",
							width: 100
						},
						{
							title: localize("_RequestedBy"),
							trackingId: "requested-by",
							width: 250,
							sortable: sortingFor("requestedBy")
						},
						{
							title: localize("_Community"),
							trackingId: "community",
							width: 250,
							sortable: sortingFor("communityName")
						},
						{
							title: localize("_RequestDate"),
							trackingId: "request-date",
							sortable: sortingFor("requestedOn")
						}
					]}
					trackingId="membership-requests-grid"
				>
					{(item) => {
						const isLoading: boolean = loadingIds.has(item.id);

						return (
							<TableView.Row trackingId={item.id.toString()}>
								<TableView.Cell>
									<div className={styles.actions}>
										<IconButton
											kind="transparent-colored"
											icon={{
												kind: "font",
												name: "check",
												color: "success"

											}}
											tooltip={localize("_Approve")}
											isDisabled={isLoading}
											onClick={() => respondHandler(item, MembershipRequestResponse.Approve)}
											trackingId="approve"
											bubbleClick={false}
										/>
										<IconButton
											kind="transparent-colored"
											icon={{
												kind: "font",
												name: "xmark",
												color: "danger"

											}}
											tooltip={localize("_Decline")}
											isDisabled={isLoading}
											onClick={() => respondHandler(item, MembershipRequestResponse.Decline)}
											trackingId="decline"
											bubbleClick={false}
										/>
									</div>
								</TableView.Cell>
								<TableView.Cell tooltip={item.requestedBy}>
									{item.requestedBy}
								</TableView.Cell>
								<TableView.Cell tooltip={item.communityName}>
									{item.communityName}
								</TableView.Cell>
								<TableView.Cell>
									<DateString
										value={item.requestedOn}
										format="DMYT"
										timezone={userTimezone}
									/>
								</TableView.Cell>
							</TableView.Row>
						);
					}}
				</TableView>
				<Pagination
					{...pagination}
					onPageNumberChanged={setCurrentPageNumber}
					trackingId="pagination"
				/>
			</HubManagementDialogContentLayout.Table>
		</HubManagementDialogContentLayout>
	);
};
CommunityMembershipRequests.displayName = "CommunityMembershipRequests";

type MembershipRequestGridItem = {
	id: number;
	requestedBy: string;
	communityName: string;
	requestedOn: Date;
};

enum MembershipRequestResponse {
	Approve = 1,
	Decline = 2
}

const useDataSource = createLocalDataSource<MembershipRequestGridItem>()
	.setConfiguration({
		search: {
			doesMatchItem: (item, searchTerm) => contains(item.requestedBy, searchTerm)
				|| contains(item.communityName, searchTerm)
		},
		hasPagination: true
	})
	.getHook();

function convertToGridItem(request: CommunityMembershipRequestListItem): MembershipRequestGridItem {
	const {
		id,
		requestedBy,
		community,
		requestedOn
	} = request;

	const { computedDisplayName } = getComputedDisplayName({
		firstName: requestedBy.firstName,
		lastName: requestedBy.lastName
	});

	return {
		id,
		requestedBy: computedDisplayName,
		communityName: community.name,
		requestedOn: parseDtoDate(requestedOn)
	};
}
